import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { MEASUREMENT_DATE_SLUG } from 'dashboard409a/common/constants';
import { clearDashboardMeasurementDate, selectDashboardMeasurementDate } from 'dashboard409a/states/features/global';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import { formatMD, getUniqueListBy, useQueryParams } from 'dashboard409a/utilities';
import { toString } from 'utilities';
import useStyles from './styles';
import { IDateFilter, IOptions } from './types';

const DateFilter: FC<IDateFilter> = props => {
  const { label, filterId, isLoading } = props;

  const [selectedValue, setSelectedValue] = useState('');

  const classes = useStyles();
  const { clearQuery, getQuery, setQuery } = useQueryParams();

  const { currentCompany, currentCompanyMeasurementDateId } = useSelector((state: RootStateType) => state.global);
  const { allValuationScalarOpinion, valuationListRequestId } = useSelector((state: RootStateType) => state.valuation);
  const dispatch = useDispatch<AppDispatchType>();

  const queryMeasurementDate = getQuery.get('md');
  const isLoadingReportDates = Boolean(valuationListRequestId);

  const options = useMemo(() => {
    const allOptions = (allValuationScalarOpinion ?? [])
      .filter(scalarOpinion => scalarOpinion.report_date.company === currentCompany?.id)
      .map(
        scalarOpinion =>
          ({
            id: scalarOpinion.report_date.measurement_date.id,
            date: scalarOpinion.report_date.measurement_date.date,
            slug:
              scalarOpinion.report_date.measurement_date.slug ??
              `${MEASUREMENT_DATE_SLUG}-${scalarOpinion.report_date.measurement_date.date}`,
            companyMeasurementDateId: scalarOpinion.report_date.id,
          }) as IOptions
      );

    return getUniqueListBy<IOptions>(allOptions, 'id').sort((a, b) => b.date.localeCompare(a.date));
  }, [allValuationScalarOpinion, currentCompany?.id]);

  const selectMeasurementDate = useCallback(
    (measurementDate: IOptions) => {
      setSelectedValue(toString(measurementDate.id));

      if (currentCompanyMeasurementDateId === measurementDate.companyMeasurementDateId) {
        return;
      }

      dispatch(
        selectDashboardMeasurementDate({
          currentMeasurementDate: {
            ...measurementDate,
            date: measurementDate.date,
            cmd_id: measurementDate.companyMeasurementDateId,
          },
          currentMeasurementDateId: measurementDate.id,
          currentCompanyMeasurementDateId: measurementDate.companyMeasurementDateId,
        })
      );
    },
    [currentCompanyMeasurementDateId, dispatch]
  );

  useEffect(() => {
    setSelectedValue('');
  }, [currentCompany?.id]);

  const setLastMeasurementDate = useCallback(() => {
    const recentMD = options[0];

    if (recentMD) {
      const queryMD = `${recentMD.date}-${recentMD.id}`;

      selectMeasurementDate(recentMD);
      setQuery('md', queryMD);
    }
  }, [options, selectMeasurementDate, setQuery]);

  // get/set MD from query params
  useEffect(() => {
    if (isLoadingReportDates) {
      return;
    }

    if (options.length === 0) {
      setSelectedValue('');

      if (currentCompanyMeasurementDateId !== undefined) {
        dispatch(clearDashboardMeasurementDate());
      }

      if (queryMeasurementDate) {
        clearQuery();
      }

      return;
    }

    if (queryMeasurementDate) {
      const currentMD = options.find(
        option =>
          `${option.date}-${option.id}` === queryMeasurementDate ||
          option.slug === queryMeasurementDate ||
          option.slug === `${MEASUREMENT_DATE_SLUG}-${queryMeasurementDate}` ||
          option.date === queryMeasurementDate
      );

      if (currentMD) {
        selectMeasurementDate(currentMD);
      } else {
        setLastMeasurementDate();
      }
    } else {
      setLastMeasurementDate();
    }
  }, [
    clearQuery,
    currentCompanyMeasurementDateId,
    dispatch,
    isLoadingReportDates,
    options,
    queryMeasurementDate,
    selectMeasurementDate,
    setLastMeasurementDate,
  ]);

  const renderValue = (value: unknown) => {
    const currentOption = options.find(option => toString(option.id) === toString(value as string | number));

    if (options.length > 0 && currentOption) {
      return formatMD({ date: currentOption.date });
    }

    return `No ${label.toLowerCase()}s available`;
  };

  const onMeasurementDateChange = (event: SelectChangeEvent<string>) => {
    const measurementDateId = event.target.value;

    if (measurementDateId) {
      const selectedMD = options.find(md => md.id === Number(measurementDateId));

      if (selectedMD) {
        const selectedMDQuery = `${selectedMD.date}-${selectedMD.id}`;

        selectMeasurementDate(selectedMD);
        setQuery('md', selectedMDQuery);
      }
    }
  };

  // Loading
  if (isLoading || isLoadingReportDates) {
    return <Skeleton variant="text" width={150} height={50} />;
  }

  return (
    <FormControl className={classes.formControl}>
      <InputLabel id={`${filterId}-label`} className={classes.label} shrink>
        {label}
      </InputLabel>
      <Select
        variant="standard"
        classes={{ root: classes.select }}
        className={classes.select}
        displayEmpty
        id={filterId}
        onChange={onMeasurementDateChange}
        renderValue={renderValue}
        value={selectedValue}>
        <MenuItem selected disabled value=" ">
          Select a {label}
        </MenuItem>
        {options.map(({ id, date }) => (
          <MenuItem value={toString(id)} key={toString(id)} id={`${filterId}-option-${id + 1}`}>
            {formatMD({ date })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default DateFilter;
